"use client";

import React, { useEffect, useRef } from "react";
import { InputField } from "@ospweb/sdk-ui";
import { cn } from "utils";

import { createInviteCodeCopy } from "./inviteCodeCopy";

/**
 * 邀请码输入框 —— Figma 填码弹层里的 `Email input (library)` 三态：
 *
 * - **空**（`110013:130027` / PC `131119`）：`bg/solid/neutral/primary` · `border/inverse/transparent10%` · r10 · py14（高 48）
 * - **有值**（预填 `130170` / 校验中 `130288`）：`bg/white/overlay5%` · `border/inverse/transparent5%` · r8 · py12（高 44）
 * - **错误**（`130039` / PC `131154`）：同「有值」，边框转 `border/accent2/solid`；下方 8px 出 helper text（`body_s`，px-4）
 */
interface InviteCodeInputFieldProps {
  value: string;
  onValueChange: (value: string) => void;
  /** 后端不认这个码 / 格式非法 → 显示错误边框与 helper text。 */
  error?: boolean;
  /** 覆盖默认错误文案（默认取 `We don't recognise that code…`）。 */
  errorMessage?: string;
  locale?: string;
  className?: string;
}

export function InviteCodeInputField({
  value,
  onValueChange,
  error = false,
  errorMessage,
  locale,
  className,
}: InviteCodeInputFieldProps) {
  const inputRef = useRef<React.ComponentRef<typeof InputField>>(null);
  const copy = createInviteCodeCopy(locale);

  useEffect(() => {
    inputRef.current?.setError?.(error);
  }, [error]);

  return (
    <div className={cn("w-full", error && "flex flex-col gap-2", className)}>
      <InputField
        ref={inputRef}
        type="text"
        inputMode="text"
        autoComplete="off"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder={copy.placeholder}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        size="small"
        containerClassName={cn(
          "flex w-full items-center border border-solid px-3",
          value
            ? "h-11 rounded-lg bg-background_white_overlay5 py-3"
            : "min-h-12 rounded-[10px] bg-background_solid_neutral_primary py-[14px]",
          error
            ? "border-border_accent2_solid"
            : value
              ? "border-border_inverse_transparent5"
              : "border-border_inverse_transparent10",
        )}
        inputClassName="min-w-0 flex-1 border-0 bg-transparent outline-none body_m text-content_primary placeholder:text-content_quaternary"
        aria-invalid={error}
        aria-describedby={error ? "invite-code-error" : undefined}
        data-testid="invite-code-input"
      />
      {error && (
        <p
          id="invite-code-error"
          className="body_s shrink-0 px-1 text-content_accent2_primary"
          role="alert"
          data-testid="invite-code-error"
        >
          {errorMessage ?? copy.invalidCode}
        </p>
      )}
    </div>
  );
}
